import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	setCookieConsent,
	type CookieConsentPreferences,
	type CookieConsentState,
} from '@/store/slices/cookie-consent.slice';
import { useUpdateCookieSettingsMutation } from '@/store/api';
import {
	CookiePreferencesDialog,
	saveConsent,
	type CookieCategoryState,
} from './cookie-preferences.shared';

const EMPTY_STATE: CookieCategoryState = {
	preference: false,
	performance: false,
	marketing: false,
};

function toCategoryState(value: CookieConsentPreferences | null): CookieCategoryState {
	if (!value) return EMPTY_STATE;
	return {
		preference: !!value.preference,
		performance: !!value.performance,
		marketing: !!value.marketing,
	};
}

interface CookieSettingsModalProps {
	open: boolean;
	onClose: () => void;
}

export function CookieSettingsModal({ open, onClose }: CookieSettingsModalProps) {
	const dispatch = useDispatch();
	const consentValue = useSelector(
		(state: { cookieConsent: CookieConsentState }) => state.cookieConsent.value
	);
	const [updateCookieSettings] = useUpdateCookieSettingsMutation();
	const [categoryState, setCategoryState] = useState<CookieCategoryState>(EMPTY_STATE);

	// Seed the toggles from the user's current consent each time the modal opens.
	useEffect(() => {
		if (open) {
			setCategoryState(toCategoryState(consentValue));
		}
	}, [open, consentValue]);

	const allSelected = Object.values(categoryState).every(Boolean);

	const handleSelectAll = (checked: boolean) => {
		setCategoryState({ preference: checked, performance: checked, marketing: checked });
	};

	const handleToggle = (key: keyof CookieCategoryState, checked: boolean) => {
		setCategoryState((prev) => ({ ...prev, [key]: checked }));
	};

	const persist = (preferences: CookieConsentPreferences) => {
		saveConsent(preferences);
		dispatch(setCookieConsent(preferences));
		updateCookieSettings(preferences);
		onClose();
	};

	const handleRejectAll = () => {
		persist({ essential: true, preference: false, performance: false, marketing: false });
	};

	const handleSave = () => {
		persist({ essential: true, ...categoryState });
	};

	if (!open) return null;

	return (
		<CookiePreferencesDialog
			categoryState={categoryState}
			allSelected={allSelected}
			onSelectAll={handleSelectAll}
			onToggle={handleToggle}
			onRejectAll={handleRejectAll}
			onAcceptAndSave={handleSave}
			onClose={onClose}
			saveLabel="Save"
		/>
	);
}
